import axios from 'axios';
import { currentProfileCache } from '@/modules/utils/currentProfileCache.js';

// The auth cookie outlives Plato's session, and LoginController redirects a signed-in visitor
// straight back here - so going to /login with the cookie still set loops forever. Sign out first.
// If the sign-out did not stick (e.g. Logout failed under load) we are back here within minutes,
// so send the visitor home instead of looping.
const loginRedirectKey = 'mmbLoginRedirectAt';
const loginRedirectWindowMs = 5 * 60 * 1000;

const redirectToLogin = () => {
  if (Date.now() - Number(sessionStorage.getItem(loginRedirectKey)) < loginRedirectWindowMs) {
    sessionStorage.removeItem(loginRedirectKey);
    document.location.href = '/';
    return Promise.resolve();
  }

  sessionStorage.setItem(loginRedirectKey, String(Date.now()));

  return axios
    .get('/umbraco/surface/authenticationapi/Logout')
    .catch(error => console.warn('Failed to logout:', error))
    .finally(() => {
      currentProfileCache.clear();
      document.location.href = '/login';
    });
};

export default {
  getHolidays({
    state,
    commit
  }) {

    return axios
      .get(state.data.myBookings.dataUrl)
      .then(response => {
        commit('setLoading', false);
        commit('setHolidays', response.data.holidays);

        if (response.data.profileDetails) {
          commit('setUserDetails', response.data.profileDetails);
        }

        if (!response.data.isAuthenticated) {
          return redirectToLogin();
        }
      });

  },

  getBooking({
    state,
    commit,
    rootState
  }) {
    return axios
      .get(state.data.overviewContent.bookingDataUrl, {
        params: {
          bookingReference: rootState.route.params.id
        }
      })
      .then(response => {
        if (!response.data.isAuthenticated) {
          return redirectToLogin();
        }
        commit('setBooking', response.data);
      });

  },

  getDepartureDetails({
    state,
    commit
  }) {

    return axios
      .get(state.data.flights.dataUrl, {
        params: {
          departureId: state.booking.departureId,
          filo: state.booking.filo,
          transportCode: state.booking.departureTransportCode
        }
      })
      .then(response => {
        commit('setDepartureDetails', response.data);
      })
      .catch(error => {
        console.error(error);
        return Promise.resolve();
      });
  },

  payment({
    commit,
    state,
    getters,
  }, data) {
    commit('setLoading', true);

    const customerId = state.booking.passengers.find(p => p.lead).passengerId;
    const brand = state.booking.brand;
    const autoBalance = Boolean(getters.takeFinalBalance);

    return axios
      .post('/umbraco/api/payment', {
        bookingId: data.bookingId,
        amount: data.amount,
        currency: data.currency.toUpperCase(),
        customerId: customerId.toString(),
        source: 'mmb',
        brand: brand,
        autoBalance: autoBalance
      })
      .then(response => {
        commit('setPaymentFrame', { url: response.data.url });
        commit('setTransId', response.data.transId);
        commit('setLoading', false);
      })
      .catch(error => {
        console.error('Payment action - Error:', error);
        commit('setError', true);
        commit('setLoading', false);
        if (error.response?.data?.type === 'UserFriendlyError') {
          commit('setErrorMessage', error.response.data.message);
        }
      });
  },

  excursions({
    state,
    commit,
    getters
  }) {
    return axios
      .post(state.data.bookingExcursions.submitUrl, getters.excursions)
      .then(response => {
        console.log(response.data);
      });
  },

  getUserDetails({
    state,
    commit
  }) {
    commit('setLoading', true);
    return axios
      .get(state.data.manageMyDetailsContent.dataUrl)
      .then(response => {
        commit('setUserDetails', response.data);
        commit('setLoading', false);
      })
      .catch(error => {
        console.error(error);
        commit('setLoading', false);
        commit('setError', true);
      });
  },

  updatePassenger({
    state,
    commit,
    dispatch,
    rootGetters,
    rootState
  }, passenger) {
    commit('setLoading', true);

    const formData = rootGetters.formDataFlat(passenger);

    return axios
      .patch(state.data.passengerDetailsContent.submitUrl, {
        ...passenger,
        emergencyContacts: passenger.emergencyContacts.filter(kin => kin.firstName),
        ...formData,
        bookingReference: rootState.route.params.id
      })
      .then(() => {
        currentProfileCache.clear();
      })
      .catch(() => {
        commit('setLoading', false);
      })
      .finally(() => {
        dispatch('getBooking').then(() => {
          commit('setLoading', false);
        });
      });
  },

  updatePassengerKin({
    state,
    commit,
    dispatch,
    rootGetters,
    rootState
  }, data) {
    commit('setLoading', true);

    const formData = rootGetters.formDataFlat(data.kin);

    // Add address into suitable format for API
    if (formData.address) {
      formData.address.addressLines = Object.keys(formData.address.addressLines).map(k => formData.address.addressLines[k]);
    }

    data.passenger.emergencyContacts[data.kinIndex] = {
      ...data.passenger.emergencyContacts[data.kinIndex],
      ...formData
    };

    return axios
      .post(state.data.passengerDetailsContent.submitUrl, {
        ...data.passenger,
        bookingReference: rootState.route.params.id
      })
      .then(() => {
        currentProfileCache.clear();
      })
      .catch(() => {
        commit('setLoading', false);
      })
      .finally(() => {
        dispatch('getBooking').then(() => {
          commit('setLoading', false);
        });
      });
  },

  updateAutoBalance({
    state,
    getters,
    commit
  }) {
    commit('setLoading', true);
    commit('setLoadingType', 'autoBalance');

    const bookingId = getters.getBookingReferenceAlias;
    const noAutoFinalPayment = Boolean(!(getters.takeFinalBalance));

    return axios
      .post(state.data.autoBalanceSubmitUrl, {
        bookingReference: bookingId,
        noAutoFinalPayment: noAutoFinalPayment,
        brand: state.booking.brand
      })
      .catch(error => {
        commit('setError', true);
      })
      .finally(() => {
        commit('setLoading', false);
        commit('setLoadingType', null);
      });
  },

  createInvoice({
    state,
    commit
  }) {
    const bookingId = state.booking.bookingReference;
    return axios
      .post(state.data.paymentsContent.createInvoiceUrl, {
        bookingReference: bookingId
      })
      .then(() => {
        commit('setExcursionAdded', false);
      })
      .catch(error => {
        console.warn(error);
        commit('setError', true);
      });
  }
}